/**
 * PHASE 7 — WebGL sky renderer.
 *
 * Draws the four sky layers in one pass per frame:
 *   0. skybox        — equirectangular galactic texture (ESA Gaia DR2)
 *   1. stars         — magnitude-proportional point sprites (HYG catalog)
 *   2. constellation lines
 *   3. deep-sky objects — ring/cross sprites
 *
 * All per-frame rotations are computed CPU-side in skyFrame.ts and uploaded
 * as one mat4 (geometry layers) plus two mat3s (skybox). No depth testing is
 * needed (everything lives on the celestial sphere).
 */

import { IDENTITY, type Quat } from "../orientation/quaternion";
import type { GlSession } from "./glSession";
import { identity, perspective, rotationFromQuat, type Mat4 } from "./mat4";
import {
  buildDeepSkyPointData,
  buildLinePointData,
  buildStarColorData,
  buildStarPointData,
  lineVertexCount,
} from "./skyData";
import { buildEnuToGalacticMatrix, buildStarProjection, transpose33 } from "./skyFrame";
import {
  DSO_FS,
  DSO_VS,
  FULLSCREEN_QUAD_INDICES,
  FULLSCREEN_QUAD_POSITIONS,
  LINES_FS,
  LINES_VS,
  SKYBOX_FS,
  SKYBOX_VS,
  STARS_FS,
  STARS_VS,
} from "./shaders";

export interface SkyFrame {
  /** Camera: world(ENU) -> device, from OrientationFusion.qCamera. */
  qCamera: Quat | null;
  latitudeDeg: number;
  localSiderealHours: number;
  /** Precession matrix J2000 -> of-date (j2000ToOfDateRotation); null = skip. */
  j2000ToOfDate: { rot: readonly (readonly number[])[] } | null;
  fovYDeg: number;
  aspect: number;
  showConstellationLines: boolean;
  showDeepSky: boolean;
  /**
   * Sun/Moon/planet sprites, in the same J2000 equatorial xyz as the stars.
   * Supplied as interleaved [x, y, z, sizePx] (positions + one size per
   * body) with matching packed RGB colors. Both arrays are 4x/3x the body
   * count; null = no dynamic bodies this frame.
   */
  bodies?: { positions: Float32Array; colors: Float32Array } | null;
  /**
   * AR mode: composite over a live camera feed. The canvas clears
   * transparently and the photographic skybox layer is suppressed (the
   * camera shows the real sky instead). The stars/lines/DSO layers are
   * unchanged and stay aligned with the existing coordinate pipeline.
   */
  arMode?: boolean;
}

const STAR_MAG_LIMIT = 6.5;
const NEAR = 0.1;
const FAR = 1000;

export class SkyRenderer {
  private gl: WebGL2RenderingContext | null;
  private program: Record<"skybox" | "stars" | "lines" | "dsos", WebGLProgram | null>;
  private buffer: Record<
    "quad" | "quadIndex" | "stars" | "starColors" | "lines" | "dsos" | "bodies" | "bodyColors",
    WebGLBuffer | null
  >;
  private vao: Record<"skybox" | "stars" | "lines" | "dsos" | "bodies", WebGLVertexArrayObject | null>;
  private texture: WebGLTexture | null = null;
  private starCount: number;
  private lineCount: number;
  private dsoCount: number;
  private bodyCount = 0;
  private bodyBufferAlloc = 0;
  private uniforms: Record<string, WebGLUniformLocation | null> = {};

  /** Cached uniform location; a missing/optimized-out uniform is null. */
  private u(name: string): WebGLUniformLocation | null {
    return this.uniforms[name] ?? null;
  }

  constructor(session: GlSession) {
    this.gl = session.gl;
    if (!this.gl) throw new Error("SkyRenderer requires a WebGL2 context");
    const gl = this.gl;

    this.program = {
      skybox: session.createProgram(SKYBOX_VS, SKYBOX_FS),
      stars: session.createProgram(STARS_VS, STARS_FS),
      lines: session.createProgram(LINES_VS, LINES_FS),
      dsos: session.createProgram(DSO_VS, DSO_FS),
    };
    if (Object.values(this.program).some((p) => p === null)) {
      throw new Error("shader compilation failed — see console for diagnostics");
    }

    // Resolve every uniform location once (perf: getUniformLocation is a
    // name-hash lookup that would otherwise run several times per frame).
    for (const [name, prog] of Object.entries(this.program)) {
      if (!prog) continue;
      for (const uniform of ["uMVP", "uSky", "uCamToWorld", "uEnuToGal", "uInvTanHalfFovY", "uAspect", "uHorizonFade", "uColor"]) {
        this.uniforms[`${name}:${uniform}`] = gl.getUniformLocation(prog, uniform);
      }
    }
    const starData = buildStarPointData(STAR_MAG_LIMIT);
    this.starCount = starData.length / 4;
    this.lineCount = lineVertexCount();
    this.dsoCount = buildDeepSkyPointData().length / 4;

    const make = (data: Float32Array | Uint16Array, target: number): WebGLBuffer | null => {
      const buf = gl.createBuffer();
      if (!buf) return null;
      gl.bindBuffer(target, buf);
      gl.bufferData(target, data, gl.STATIC_DRAW);
      return buf;
    };
    this.buffer = {
      quad: make(FULLSCREEN_QUAD_POSITIONS, gl.ARRAY_BUFFER),
      quadIndex: make(FULLSCREEN_QUAD_INDICES, gl.ELEMENT_ARRAY_BUFFER),
      stars: make(starData, gl.ARRAY_BUFFER),
      starColors: make(buildStarColorData(STAR_MAG_LIMIT), gl.ARRAY_BUFFER),
      lines: make(buildLinePointData(), gl.ARRAY_BUFFER),
      dsos: make(buildDeepSkyPointData(), gl.ARRAY_BUFFER),
      bodies: gl.createBuffer(),
      bodyColors: gl.createBuffer(),
    };

    this.vao = {
      skybox: linkQuad(gl, this.program.skybox!, this.buffer.quad!, this.buffer.quadIndex!),
      // Star buffer is interleaved [x, y, z, sizePx] (stride 16 bytes): bind
      // aPos and aSize from it, aColor from its own packed vec3 buffer.
      stars: linkPoints(gl, this.program.stars!, [
        { name: "aPos", size: 3, buffer: this.buffer.stars!, stride: 16, offset: 0 },
        { name: "aSize", size: 1, buffer: this.buffer.stars!, stride: 16, offset: 12 },
        { name: "aColor", size: 3, buffer: this.buffer.starColors!, stride: 0, offset: 0 },
      ]),
      lines: linkPoints(gl, this.program.lines!, [
        { name: "aPos", size: 3, buffer: this.buffer.lines!, stride: 0, offset: 0 },
      ]),
      // DSO buffer is interleaved [x, y, z, sizePx] like the stars.
      dsos: linkPoints(gl, this.program.dsos!, [
        { name: "aPos", size: 3, buffer: this.buffer.dsos!, stride: 16, offset: 0 },
        { name: "aSize", size: 1, buffer: this.buffer.dsos!, stride: 16, offset: 12 },
      ]),
      // Dynamic bodies reuse the star shader and its attribute layout.
      bodies: linkPoints(gl, this.program.stars!, [
        { name: "aPos", size: 3, buffer: this.buffer.bodies!, stride: 16, offset: 0 },
        { name: "aSize", size: 1, buffer: this.buffer.bodies!, stride: 16, offset: 12 },
        { name: "aColor", size: 3, buffer: this.buffer.bodyColors!, stride: 0, offset: 0 },
      ]),
    };
  }

  /**
   * Load the equirectangular sky texture. A failure leaves the skybox black
   * but does not break the frame.
   */
  loadTexture(url: string): Promise<boolean> {
    const gl = this.gl;
    if (!gl) return Promise.resolve(false);
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        if (!this.texture) {
          const tex = gl.createTexture();
          if (!tex) {
            resolve(false);
            return;
          }
          this.texture = tex;
        }
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false); // v = 0 is the top of the image (NGP)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        gl.generateMipmap(gl.TEXTURE_2D);
        resolve(true);
      };
      image.onerror = () => resolve(false);
      image.src = url;
    });
  }

  frame(f: SkyFrame): void {
    const gl = this.gl;
    if (!gl) return;
    const q = f.qCamera ?? IDENTITY;
    const rotor = f.j2000ToOfDate;
    const projection = perspective(f.fovYDeg, f.aspect, NEAR, FAR);
    const starMvp = buildStarProjection(projection, q, f.latitudeDeg, f.localSiderealHours, rotor);

    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    if (f.arMode) {
      gl.clearColor(0, 0, 0, 0);
    } else {
      gl.clearColor(0.004, 0.006, 0.014, 1);
    }
    gl.clear(gl.COLOR_BUFFER_BIT);

    if (!f.arMode) this.drawSkybox(gl, f);
    this.drawStarField(gl, starMvp);
    if (f.showConstellationLines && this.lineCount > 0) this.drawLines(gl, starMvp);
    if (f.showDeepSky && this.dsoCount > 0) this.drawDsos(gl, starMvp);
    if (f.bodies && f.bodies.positions.length > 0) this.drawBodies(gl, starMvp, f.bodies);
  }

  /**
   * Upload the dynamic-body sprite buffer. Data changes only when the
   * cached positions refresh (~2 s), so the upload is skipped entirely on
   * the frames in between (perf: no per-frame bufferData for the 9 bodies).
   */
  setBodyData(data: { positions: Float32Array; colors: Float32Array } | null): void {
    const gl = this.gl;
    if (!gl || !data) {
      this.bodyCount = 0;
      return;
    }
    const count = data.positions.length / 4;
    const needAlloc = this.bodyBufferAlloc < data.positions.length;
    this.bodyBufferAlloc = Math.max(this.bodyBufferAlloc, data.positions.length);
    if (count !== this.bodyCount) {
      this.bodyCount = count;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.bodies);
      gl.bufferData(gl.ARRAY_BUFFER, data.positions, gl.DYNAMIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.bodyColors);
      gl.bufferData(gl.ARRAY_BUFFER, data.colors, gl.DYNAMIC_DRAW);
      return;
    }
    if (needAlloc) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.bodies);
      gl.bufferData(gl.ARRAY_BUFFER, data.positions, gl.DYNAMIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.bodyColors);
      gl.bufferData(gl.ARRAY_BUFFER, data.colors, gl.DYNAMIC_DRAW);
    } else {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.bodies);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, data.positions);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.bodyColors);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, data.colors);
    }
  }

  private drawSkybox(gl: WebGL2RenderingContext, f: SkyFrame): void {
    const program = this.program.skybox;
    if (!program || !this.texture) return;
    gl.useProgram(program);
    gl.bindVertexArray(this.vao.skybox);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    uniform1i(gl, this.u("skybox:uSky"), 0);
    const camToWorld = f.qCamera ? transpose33(rotationFromQuat(f.qCamera)) : identity();
    uniformM3(gl, this.u("skybox:uCamToWorld"), camToWorld);
    uniformM3(gl, this.u("skybox:uEnuToGal"), buildEnuToGalacticMatrix(f.latitudeDeg, f.localSiderealHours, f.j2000ToOfDate));
    uniform1f(gl, this.u("skybox:uInvTanHalfFovY"), 1 / Math.tan(((f.fovYDeg / 2) * Math.PI) / 180));
    uniform1f(gl, this.u("skybox:uAspect"), f.aspect);
    uniform1f(gl, this.u("skybox:uHorizonFade"), 0.06);
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    gl.bindVertexArray(null);
  }

  private drawStarField(gl: WebGL2RenderingContext, mvp: Mat4): void {
    this.drawPoints(gl, this.program.stars!, this.vao.stars, this.starCount, this.u("stars:uMVP"), mvp);
  }

  private drawPoints(
    gl: WebGL2RenderingContext,
    program: WebGLProgram,
    vao: WebGLVertexArrayObject | null,
    count: number,
    mvpLocation: WebGLUniformLocation | null,
    mvp: Mat4,
  ): void {
    if (count === 0) return;
    gl.useProgram(program);
    gl.bindVertexArray(vao);
    gl.uniformMatrix4fv(mvpLocation, false, mvp);
    gl.drawArrays(gl.POINTS, 0, count);
    gl.bindVertexArray(null);
  }

  private drawBodies(
    gl: WebGL2RenderingContext,
    mvp: Mat4,
    data: { positions: Float32Array; colors: Float32Array },
  ): void {
    const count = data.positions.length / 4;
    if (count === 0) return;
    this.setBodyData(data);
    gl.useProgram(this.program.stars!);
    gl.bindVertexArray(this.vao.bodies);
    gl.uniformMatrix4fv(this.u("stars:uMVP"), false, mvp);
    gl.drawArrays(gl.POINTS, 0, count);
    gl.bindVertexArray(null);
  }

  private drawLines(gl: WebGL2RenderingContext, mvp: Mat4): void {
    const program = this.program.lines;
    if (!program) return;
    gl.useProgram(program);
    gl.bindVertexArray(this.vao.lines);
    gl.uniformMatrix4fv(this.u("lines:uMVP"), false, mvp);
    gl.uniform4f(this.u("lines:uColor"), 0.55, 0.62, 0.85, 0.5);
    gl.drawArrays(gl.LINES, 0, this.lineCount);
    gl.bindVertexArray(null);
  }

  private drawDsos(gl: WebGL2RenderingContext, mvp: Mat4): void {
    const program = this.program.dsos;
    if (!program) return;
    gl.useProgram(program);
    gl.bindVertexArray(this.vao.dsos);
    gl.uniformMatrix4fv(this.u("dsos:uMVP"), false, mvp);
    gl.uniform3f(this.u("dsos:uColor"), 0.85, 0.9, 1.0);
    gl.drawArrays(gl.POINTS, 0, this.dsoCount);
    gl.bindVertexArray(null);
  }
}

function uniform1i(gl: WebGL2RenderingContext, location: WebGLUniformLocation | null, v: number): void {
  if (!location) return;
  gl.uniform1i(location, v);
}

function uniform1f(gl: WebGL2RenderingContext, location: WebGLUniformLocation | null, v: number): void {
  if (!location) return;
  gl.uniform1f(location, v);
}

/** Upload the upper-left 3x3 of a column-major Mat4 as a mat3. */
function uniformM3(gl: WebGL2RenderingContext, location: WebGLUniformLocation | null, m: Mat4): void {
  if (!location) return;
  gl.uniformMatrix3fv(location, false, new Float32Array([
    m[0]!, m[1]!, m[2]!, m[4]!, m[5]!, m[6]!, m[8]!, m[9]!, m[10]!,
  ]));
}

function linkQuad(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  positions: WebGLBuffer,
  index: WebGLBuffer,
): WebGLVertexArrayObject | null {
  const vao = gl.createVertexArray();
  if (!vao) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, positions);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, index);
  const loc = gl.getAttribLocation(program, "aPos");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return vao;
}

/**
 * Bind a set of attributes (each from its own buffer, with its own stride and
 * byte offset) into a fresh VAO. The declared-but-unused attributes of the
 * program are left unbound — the shaders must only read attributes listed
 * here, or the vertex data would be garbage.
 */
function linkPoints(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  attributes: { name: string; size: number; buffer: WebGLBuffer; stride: number; offset: number }[],
): WebGLVertexArrayObject | null {
  const vao = gl.createVertexArray();
  if (!vao) return null;
  gl.bindVertexArray(vao);
  for (const attr of attributes) {
    gl.bindBuffer(gl.ARRAY_BUFFER, attr.buffer);
    const loc = gl.getAttribLocation(program, attr.name);
    if (loc < 0) continue; // optimized out — nothing to bind
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, attr.size, gl.FLOAT, false, attr.stride, attr.offset);
  }
  gl.bindVertexArray(null);
  return vao;
}